import { Injectable, Injector } from '@angular/core';
import { environment } from '@environment/environment';
import { Store } from '@ngrx/store';
import { DatasetService, SettingService } from 'data';
import { catchError, first, map, switchMap, tap } from 'rxjs/operators';
import { loadDataSetsSuccess } from '../state/data-set/data-set.actions';
import { selectDataSet } from '../state/data-set/data-set.actions';
import { DataSet } from '../state/data-set/data-set.state';
import { PaymentWrapperService } from './service-wrappers/payment-wrapper.service';
import { ConfigurationWrapperService } from './service-wrappers/configuration-wrapper.service';
import { SessionStorageService } from './session-storage.service';
import { actionSettingsChangeTheme } from '@core/state/settings/settings.actions';
import { of } from 'rxjs';

const SESSION_STORAGE_IMAGINEPAY_ACTIVE = 'imaginepay-active';
const DOCUMENTS_ACTIVE = "documents-active"
const REMITTANCE_ACTIVE = "remittance-active"
const UPLOAD_CUSTOMIZATION = "upload-customization"
const CUSTOM_LOGO = "custom-logo"
const DEFAULT_THEME = "default-theme";
const DEFAULT_THEME_LEVEL = "default-theme-level";
const PEREGRINE_CLIENT_ID = "peregrine-client-id";
const EVERYWARE_ACTIVE = "everyware-active"

@Injectable()
export class AppInitializerService {
  private appConfig;
  private storeObject;
  private currentTheme: string;
  constructor(
    private injector: Injector,
    private store: Store,
    private dataSetService: DatasetService,
    private sessionStorageService: SessionStorageService,
    private paymentService: PaymentWrapperService,
    private configurationService: ConfigurationWrapperService
  ) {
    this.store.subscribe((result) => {
      this.storeObject = result;
      this.currentTheme = this.storeObject?.settings?.theme;
    });
  }

  initialize() {
      return this.dataSetService.apiV1DatasetAllUserPost({ pageSize: 100 }).pipe(
        tap((response) => {
          const clients = response.data;
          let existingSelectedClientId = this.sessionStorageService.getItem('selected-data-set-id');
          if ((existingSelectedClientId === '' || existingSelectedClientId === null) && clients?.length > 0) {
            existingSelectedClientId = clients[0].id;
            this.sessionStorageService.setItem('selected-data-set-id', existingSelectedClientId);
            this.sessionStorageService.setItem('institutional-billing', clients[0].institutionalBilling);
            this.sessionStorageService.setItem('automated-card-processing', clients[0].automatedCardProcessing);
          } else if (!clients?.find((x) => x.id === existingSelectedClientId) && clients.length > 0) {
            existingSelectedClientId = clients[0].id;
            this.sessionStorageService.setItem('selected-data-set-id', existingSelectedClientId);
            this.sessionStorageService.setItem('institutional-billing', clients[0].institutionalBilling);
            this.sessionStorageService.setItem('automated-card-processing', clients[0].automatedCardProcessing);
          } else {
            let existingDataset = clients?.find((x) => x.id === existingSelectedClientId);
            this.sessionStorageService.setItem('institutional-billing', existingDataset?.institutionalBilling ?? false);
            this.sessionStorageService.setItem('automated-card-processing', existingDataset?.automatedCardProcessing ?? false);
          }
          this.store.dispatch(selectDataSet({ dataSetId: existingSelectedClientId }));

          this.store.dispatch(loadDataSetsSuccess({ dataSets: clients as DataSet[] }));
          this.loadFeatureConfiguration();
        }),
        switchMap((response) =>
          this.paymentService.apiV1PaymentConfigPost().pipe(
            first(),
            tap((result) => this.applyImaginePayConfiguration(result)),
            catchError(() => {
              this.applyImaginePayConfiguration();
              return of(null);
            }),
            map(() => response)
          )
        )
      );
  }

  private applyImaginePayConfiguration(result?: { imaginePayDataset?: string | null; clientId?: string | null; password?: string | null }) {
    const ipActive = result?.imaginePayDataset != null;
    this.sessionStorageService.setItem(SESSION_STORAGE_IMAGINEPAY_ACTIVE, ipActive);
    this.sessionStorageService.removeItem('IMAGINE_PAY_TOKEN');
    this.sessionStorageService.removeItem('IMAGINE_PAY_TOKEN_EXP');
    environment.imaginePayConfig.authUserName = result?.imaginePayDataset ?? '';
    environment.imaginePayConfig.authClientId = result?.clientId ?? '';
    environment.imaginePayConfig.authPassword = result?.password ?? '';
  }

  private loadFeatureConfiguration() {
    this.configurationService.apiV1ConfigurationFeatureFeaturenameGet("documents").subscribe(f => {
      let documents_active = f.featureLevel ?? 0 > 0;
      this.sessionStorageService.setItem(DOCUMENTS_ACTIVE, documents_active);
    });
    this.configurationService.apiV1ConfigurationFeatureFeaturenameGet("everyware").subscribe(f => {
      let everyware_active = f.featureLevel ?? 0 > 0;
      this.sessionStorageService.setItem(EVERYWARE_ACTIVE, everyware_active);
    });
    this.configurationService.apiV1ConfigurationFeatureFeaturenameGet("remittance-upload").subscribe(f => {
      let remittance_active = f.featureLevel ?? 0 > 0;
      this.sessionStorageService.setItem(REMITTANCE_ACTIVE, remittance_active);
    });
    this.configurationService.apiV1ConfigurationUploadGet().subscribe(u => {
      this.sessionStorageService.setItem(UPLOAD_CUSTOMIZATION, u);
    });
    this.configurationService.apiV1ConfigurationFeatureFeaturenameGet("custom-logo").subscribe(f => {
      if (f.featureLevel > 0) {
        this.sessionStorageService.setItem(CUSTOM_LOGO, f.featureValue);
      }
    });
    this.configurationService.apiV1ConfigurationFeatureFeaturenameGet("default-theme").subscribe(f => {
      this.sessionStorageService.setItem(DEFAULT_THEME, f.featureValue);
      this.sessionStorageService.setItem(DEFAULT_THEME_LEVEL, f.featureLevel);
      if (f.featureValue !== '' &&
        (f.featureLevel === 1 && this.currentTheme != f.featureValue || f.featureLevel === 2 && this.currentTheme === '')
      ) {
        this.store.dispatch(actionSettingsChangeTheme({ theme: f.featureValue }));
      }
    });
    this.configurationService.apiV1ConfigurationFeatureFeaturenameGet("peregrine-client-id").subscribe(f => {
      if (f.featureLevel > 0) {
        this.sessionStorageService.setItem(PEREGRINE_CLIENT_ID, f.featureValue);
      }
    });
  }

  getConfig() {
    return this.appConfig;
  }
}
